/**
 * /admin/support — Stripe-style support ticket inbox.
 * - Filter chips by status (with counts)
 * - Search by email / reference / subject
 * - Status pills + one-click flip to open / answered / closed
 * - Optional internal note appended to audit trail
 * - Copy-to-clipboard for the customer email so admin can reply quickly
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Shield, RefreshCw, LogOut, Loader2, AlertCircle, CheckCircle2, Clock,
  Search, Inbox, Copy, Mail, MessageSquare, ChevronDown, ChevronUp,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_KEY_LS = 'nofee_admin_key';

const STATE_META = {
  open:     { label: 'Open',     pill: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
  answered: { label: 'Answered', pill: 'bg-sky-500/15 text-sky-300 border-sky-500/40' },
  closed:   { label: 'Closed',   pill: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
};

const FILTERS = [
  { key: 'all',      label: 'All' },
  { key: 'open',     label: 'Open' },
  { key: 'answered', label: 'Answered' },
  { key: 'closed',   label: 'Closed' },
];

const CATEGORY_LABELS = {
  general: 'General', order_issue: 'Order issue', rewards: 'Rewards',
  restaurant: 'Restaurant', billing: 'Billing', other: 'Other',
};

const timeAgo = (iso) => {
  if (!iso) return '';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return iso; }
};

const SupportAdminPage = () => {
  const [adminKey, setAdminKey] = useState(() => { try { return localStorage.getItem(ADMIN_KEY_LS) || ''; } catch { return ''; } });
  const [authed, setAuthed] = useState(!!adminKey);
  const [keyInput, setKeyInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({ tickets: [], counts: {} });
  const [filter, setFilter] = useState('open');
  const [query, setQuery] = useState('');
  const [actingId, setActingId] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');

  const fetchData = async (key, status = filter) => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/admin/support/tickets`, {
        headers: { 'X-Admin-Key': key },
        params: status && status !== 'all' ? { status, limit: 200 } : { limit: 200 },
      });
      setData(r.data);
      setAuthed(true);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 403) {
        setError('Invalid admin key.');
        setAuthed(false);
        try { localStorage.removeItem(ADMIN_KEY_LS); } catch {}
      } else {
        setError(err?.response?.data?.detail || 'Could not load tickets.');
      }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (adminKey) fetchData(adminKey, filter);
    // eslint-disable-next-line
  }, []);

  const submitKey = (e) => {
    e.preventDefault();
    const k = keyInput.trim();
    if (!k) return;
    try { localStorage.setItem(ADMIN_KEY_LS, k); } catch {}
    setAdminKey(k);
    fetchData(k, filter);
  };

  const logout = () => {
    try { localStorage.removeItem(ADMIN_KEY_LS); } catch {}
    setAdminKey(''); setAuthed(false); setData({ tickets: [], counts: {} });
  };

  const onFilterChange = (k) => { setFilter(k); fetchData(adminKey, k); };

  const updateStatus = async (ticket, newStatus) => {
    setActingId(ticket.id);
    try {
      await axios.patch(
        `${API}/admin/support/tickets/${ticket.id}/status`,
        { status: newStatus, internal_note: noteDraft.trim() || null },
        { headers: { 'X-Admin-Key': adminKey } },
      );
      setNoteDraft('');
      await fetchData(adminKey, filter);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Update failed.');
    } finally { setActingId(''); }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch {}
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data.tickets || [];
    return (data.tickets || []).filter((t) =>
      [t.email, t.reference_id, t.subject, t.message, t.name].some((f) =>
        (f || '').toLowerCase().includes(q),
      ),
    );
  }, [data.tickets, query]);

  // ────────── Auth gate ──────────
  if (!authed) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <form
          onSubmit={submitKey}
          data-testid="support-admin-auth-form"
          className="w-full max-w-md p-7 rounded-3xl border border-white/10 bg-white/[0.02]"
        >
          <div className="flex items-center gap-2 mb-2">
            <Shield className="w-5 h-5 text-yellow-300" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-yellow-300 font-bold">Admin · Support inbox</span>
          </div>
          <h1 className="font-['Outfit',_Inter,_sans-serif] text-2xl font-semibold tracking-tight mb-1">Sign in to continue</h1>
          <p className="text-[13px] text-white/55 mb-5">Enter the NoFee admin key.</p>
          <input
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="Admin key"
            data-testid="support-admin-key-input"
            className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/12 text-white text-[14px] focus:outline-none focus:border-yellow-500/55"
          />
          {error && <p className="mt-3 text-[12.5px] text-rose-300">{error}</p>}
          <button
            type="submit"
            data-testid="support-admin-key-submit"
            className="mt-4 w-full inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[14px]"
          >
            Sign in
          </button>
        </form>
      </div>
    );
  }

  // ────────── Inbox ──────────
  return (
    <div className="min-h-screen bg-black text-white" data-testid="support-admin-page">
      {/* Top bar */}
      <header className="sticky top-0 z-10 bg-black/85 backdrop-blur-md border-b border-white/[0.06]">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-yellow-300" />
            <h1 className="text-[16px] font-bold tracking-tight">Support inbox</h1>
            <span className="ml-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border border-yellow-500/30 bg-yellow-500/[0.08] text-[10px] uppercase tracking-[0.2em] text-yellow-300 font-bold">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fetchData(adminKey, filter)}
              disabled={loading}
              data-testid="support-admin-refresh"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/12 hover:border-yellow-500/40 text-white/85 text-[12.5px] font-bold disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Refresh
            </button>
            <button
              type="button"
              onClick={logout}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/12 hover:border-rose-500/40 text-white/65 hover:text-rose-300 text-[12.5px] font-bold"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters + search */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
          <div className="flex gap-1.5 flex-wrap" data-testid="support-admin-filters">
            {FILTERS.map((f) => {
              const c = data.counts?.[f.key];
              const active = filter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => onFilterChange(f.key)}
                  data-testid={`support-filter-${f.key}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold transition-all border ${
                    active
                      ? 'bg-yellow-500/10 border-yellow-500/45 text-yellow-200'
                      : 'border-white/10 bg-white/[0.02] text-white/65 hover:border-yellow-500/30 hover:text-white'
                  }`}
                >
                  {f.label}
                  {typeof c === 'number' && (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] tabular-nums ${active ? 'bg-yellow-500/20' : 'bg-white/[0.05]'}`}>{c}</span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="sm:ml-auto relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search email, ref, subject…"
              data-testid="support-admin-search"
              className="w-full sm:w-72 pl-10 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-white text-[13px] placeholder-white/35 focus:outline-none focus:border-yellow-500/55"
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl border border-rose-500/30 bg-rose-500/[0.06] text-rose-300 text-[13px] flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        {/* List */}
        {loading && (data.tickets || []).length === 0 ? (
          <div className="text-white/45 text-[14px]" data-testid="support-admin-loading">Loading…</div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="support-admin-empty"
            className="p-8 sm:p-12 rounded-3xl border border-white/10 bg-white/[0.02] text-center"
          >
            <Inbox className="w-9 h-9 text-yellow-300/65 mx-auto mb-4" />
            <h2 className="text-[16px] font-bold tracking-tight">No tickets in this view</h2>
            <p className="mt-2 text-[13px] text-white/55">Try changing the filter or clearing the search.</p>
          </div>
        ) : (
          <ul className="space-y-2.5" data-testid="support-admin-list">
            {filtered.map((t) => {
              const meta = STATE_META[t.status] || STATE_META.open;
              const isOpen = expanded === t.id;
              return (
                <li
                  key={t.id}
                  data-testid={`support-ticket-row-${t.id}`}
                  className="rounded-2xl border border-white/8 bg-white/[0.015] hover:border-yellow-500/25 transition-all overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => { setExpanded(isOpen ? null : t.id); setNoteDraft(''); }}
                    className="w-full text-left p-4 sm:p-5"
                  >
                    <div className="flex items-start gap-3 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider ${meta.pill}`}>
                        {meta.label}
                      </span>
                      <code className="text-[11.5px] font-mono text-yellow-300/85 px-1.5 py-0.5 rounded bg-yellow-500/[0.04] border border-yellow-500/15">{t.reference_id}</code>
                      <span className="text-[11.5px] text-white/40">{CATEGORY_LABELS[t.category] || t.category}</span>
                      <span className="text-[11.5px] text-white/40 ml-auto">{timeAgo(t.created_at)}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <span className="text-[14.5px] font-bold tracking-tight">{t.subject || '(no subject)'}</span>
                    </div>
                    <div className="mt-1 text-[12.5px] text-white/55 flex items-center gap-2 flex-wrap">
                      <span>{t.name || '—'}</span>
                      <span className="text-white/25">·</span>
                      <span className="text-white/80">{t.email}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); copy(t.email); }}
                        className="ml-1 text-white/40 hover:text-yellow-300"
                        aria-label="Copy email"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="mt-2 flex justify-end">
                      {isOpen ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-4 sm:px-5 pb-5 border-t border-white/[0.06] pt-4" data-testid={`support-ticket-expanded-${t.id}`}>
                      <div className="p-4 rounded-xl border border-white/8 bg-black/40 mb-4">
                        <div className="text-[10.5px] uppercase tracking-[0.18em] text-yellow-300 font-bold mb-2 flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" /> Customer message
                        </div>
                        <p className="text-[13.5px] text-white/85 leading-relaxed whitespace-pre-wrap">{t.message}</p>
                      </div>

                      {Array.isArray(t.internal_notes) && t.internal_notes.length > 0 && (
                        <div className="p-3.5 rounded-xl border border-white/8 bg-white/[0.02] mb-4">
                          <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/50 font-bold mb-2">Internal notes ({t.internal_notes.length})</div>
                          <ul className="space-y-1.5">
                            {t.internal_notes.slice().reverse().map((n, i) => (
                              <li key={i} className="text-[12px] text-white/65">
                                <span className="text-white/35 mr-2">{timeAgo(n.at)}</span>{n.text}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <textarea
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        placeholder="Optional internal note (not sent to customer)"
                        rows={2}
                        maxLength={500}
                        data-testid={`support-internal-note-${t.id}`}
                        className="w-full px-3 py-2 rounded-xl bg-white/[0.03] border border-white/10 text-white text-[13px] placeholder-white/30 focus:outline-none focus:border-yellow-500/55 mb-3 resize-none"
                      />

                      <div className="flex flex-wrap gap-2">
                        <a
                          href={`mailto:${t.email}?subject=Re:%20${encodeURIComponent(t.subject || 'Your NoFee support request')}%20-%20${t.reference_id}`}
                          data-testid={`support-reply-mail-${t.id}`}
                          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[12.5px]"
                        >
                          <Mail className="w-3.5 h-3.5" /> Reply by email
                        </a>
                        {['open', 'answered', 'closed'].map((s) => (
                          <button
                            key={s}
                            type="button"
                            disabled={actingId === t.id || t.status === s}
                            onClick={() => updateStatus(t, s)}
                            data-testid={`support-mark-${s}-${t.id}`}
                            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border text-[12.5px] font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                              t.status === s
                                ? 'border-yellow-500/45 bg-yellow-500/[0.06] text-yellow-200 cursor-not-allowed'
                                : 'border-white/12 hover:border-yellow-500/40 text-white/80 hover:text-white'
                            }`}
                          >
                            {actingId === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (
                              s === 'open' ? <Clock className="w-3.5 h-3.5" /> :
                              s === 'answered' ? <CheckCircle2 className="w-3.5 h-3.5" /> :
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            )}
                            Mark {STATE_META[s].label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
};

export default SupportAdminPage;
